import React, { useState, useEffect } from 'react';
import { useUser } from '../../store/UserContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  UserIcon, 
  AcademicCapIcon, 
  BookmarkIcon,
  //CogIcon,
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  PencilIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const CandidateProfile = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form data state
  const [formData, setFormData] = useState({
    candidate_name: '',
    candidate_emailaddress: '',
    candidate_mobilenumber: '',
    candidate_gender: '',
    candidate_main_category: '',
    candidate_preferred_colleges: [],
    candidate_preferred_courses: [],
    candidate_preferred_state: [],
    candidate_dob: '',
    candidate_address: '',
    candidate_city: '',
    candidate_pincode: '',
    candidate_qualification: '',
    candidate_marks_12th: '',
    candidate_marks_10th: '',
    candidate_exam_appearing: '',
    candidate_rank: '',
    candidate_income: '',
    candidate_father_name: '',
    candidate_mother_name: '',
    candidate_father_occupation: '',
    candidate_mother_occupation: '',
    candidate_hsc_district: '',
    candidate_home_university: ''
  });

  // Dropdown options
  const [colleges, setColleges] = useState([]);
  const [courses, setCourses] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Fetch dropdown options and existing profile data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch dropdown options
        const [collegesRes, coursesRes, statesRes, citiesRes] = await Promise.all([
          axios.get('/api/colleges/'),
          axios.get('/api/courses/'),
          axios.get('/api/states/'),
          axios.get('/api/cities/?state=Maharashtra')
        ]);
        
        setColleges(collegesRes.data);
        setCourses(coursesRes.data);
        setStates(statesRes.data);
        setCities(citiesRes.data);

        // Fetch existing profile data if user is logged in
        if (user) {
          try {
            const profileRes = await axios.get('/api/candidate-profile/', {
              headers: { Authorization: `Bearer ${user.token}` }
            });
            if (profileRes.data) {
              setFormData(profileRes.data);
            }
          } catch (profileError) {
            console.log('No existing profile found, using defaults');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMultiChange = (name, selectedOptions) => {
    const values = Array.from(selectedOptions, opt => opt.value);
    setFormData(prev => ({
      ...prev,
      [name]: values
    }));
  };

  const handleDistrictChange = (e) => {
    const selectedCityId = e.target.value;
    const selectedCity = cities.find(city => city.id === parseInt(selectedCityId));
    
    setFormData(prev => ({
      ...prev,
      candidate_hsc_district: selectedCityId,
      candidate_home_university: selectedCity ? selectedCity.city_university : ''
    }));
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post('/api/candidate-profile/', formData, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      
      setSuccess('Profile saved successfully!');
      setIsEditing(false);
      
      // Show success message for 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError('');
    setSuccess('');
    // Reset form data to original values
    // In a real app, you'd want to store the original values
  };

  const handleEditProfile = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  // Styling classes matching login/signup screen
  const buttonClass = `m-2 p-2 h-10 w-fit bg-transparent text-primary-900 hover:text-white hover:bg-primary-900 border-2 rounded-lg text-base font-bold tracking-widest normal-case items-center rounded-full shadow-md transition`;
  const fieldClass = "w-full bg-white m-1 p-2 text-sm font-nunito placeholder:text-gray-500 rounded";

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please Log In</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to access your candidate profile.</p>
          <button
            onClick={() => navigate('/login')}
            className={`${buttonClass} w-full`}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-6">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Candidate Profile</h1>
              <p className="text-gray-600">Manage your academic and personal information</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Column - Profile Form */}
          <div className="lg:col-span-3 space-y-6">
            {/* Personal Information */}
            <div className="bg-primary-100 rounded-lg shadow-lg border border-gray-200 p-6 relative">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
                  <UserIcon className="w-5 h-5 mr-2" />
                  Personal Information
                </h3>
                {!isEditing ? (
                  <button
                    onClick={handleEditProfile}
                    className="p-2 bg-primary-900 text-white rounded-full hover:bg-primary-800 transition-colors"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      className={`${buttonClass} ${isSaving ? 'opacity-50' : ''}`}
                    >
                      <CheckIcon className="w-4 h-4 mr-2" />
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className={`${buttonClass} bg-gray-200 text-gray-800 hover:bg-gray-300`}
                    >
                      <XMarkIcon className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name as in *
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="candidate_name"
                      value={formData.candidate_name}
                      onChange={handleChange}
                      className={fieldClass}
                      required
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_name || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      name="candidate_emailaddress"
                      value={formData.candidate_emailaddress}
                      onChange={handleChange}
                      className={fieldClass}
                      required
                    />
                  ) : (
                    <p className="text-gray-900 flex items-center">
                      <EnvelopeIcon className="w-4 h-4 mr-2" />
                      {formData.candidate_emailaddress || 'Not provided'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number *
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="candidate_mobilenumber"
                      value={formData.candidate_mobilenumber}
                      onChange={handleChange}
                      className={fieldClass}
                      required
                    />
                  ) : (
                    <p className="text-gray-900 flex items-center">
                      <PhoneIcon className="w-4 h-4 mr-2" />
                      {formData.candidate_mobilenumber || 'Not provided'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth
                  </label>
                  {isEditing ? (
                    <input
                      type="date"
                      name="candidate_dob"
                      value={formData.candidate_dob}
                      onChange={handleChange}
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_dob || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender
                  </label>
                  {isEditing ? (
                    <select
                      name="candidate_gender"
                      value={formData.candidate_gender}
                      onChange={handleChange}
                      className={fieldClass}
                    >
                      <option value="">Select Gender</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                      <option value="O">Other</option>
                    </select>
                  ) : (
                    <p className="text-gray-900">{formData.candidate_gender || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  {isEditing ? (
                    <select
                      name="candidate_main_category"
                      value={formData.candidate_main_category}
                      onChange={handleChange}
                      className={fieldClass}
                    >
                      <option value="">Select Category</option>
                      <option value="OPEN">OPEN</option>
                      <option value="SEBC">SEBC</option>
                      <option value="OBC">OBC</option>
                      <option value="SC">SC</option>
                      <option value="ST">ST</option>
                    </select>
                  ) : (
                    <p className="text-gray-900">{formData.candidate_main_category || 'Not provided'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Academic Information */}
            <div className="bg-primary-100 rounded-lg shadow-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-6 flex items-center">
                <AcademicCapIcon className="w-5 h-5 mr-2" />
                Academic Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Qualification
                  </label>
                  {isEditing ? (
                    <select
                      name="candidate_qualification"
                      value={formData.candidate_qualification}
                      onChange={handleChange}
                      className={fieldClass}
                    >
                      <option value="">Select Qualification</option>
                      <option value="10th">10th Standard</option>
                      <option value="12th">12th Standard</option>
                      <option value="Graduation">Graduation</option>
                      <option value="Post Graduation">Post Graduation</option>
                    </select>
                  ) : (
                    <p className="text-gray-900">{formData.candidate_qualification || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Which district did you complete your 12th (HSC) from?
                  </label>
                  {isEditing ? (
                    <select
                      name="candidate_hsc_district"
                      value={formData.candidate_hsc_district}
                      onChange={handleDistrictChange}
                      className={fieldClass}
                    >
                      <option value="">Select District</option>
                      {cities.map(city => (
                        <option key={city.id} value={city.id}>
                          {city.city_names && city.city_names.length > 0 ? city.city_names[0] : city.city_code}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-gray-900">
                      {formData.candidate_hsc_district ? 
                        (cities.find(city => city.id === parseInt(formData.candidate_hsc_district))?.city_names?.[0] || 'Selected district') 
                        : 'Not provided'
                      }
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Home University
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="candidate_home_university"
                      value={formData.candidate_home_university}
                      onChange={handleChange}
                      className={fieldClass}
                      placeholder="Auto-populated from district selection"
                      readOnly
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_home_university || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    12th Standard Marks (%)
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="candidate_marks_12th"
                      value={formData.candidate_marks_12th}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_marks_12th ? `${formData.candidate_marks_12th}%` : 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    10th Standard Marks (%)
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="candidate_marks_10th"
                      value={formData.candidate_marks_10th}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_marks_10th ? `${formData.candidate_marks_10th}%` : 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exam Appearing For
                  </label>
                  {isEditing ? (
                    <select
                      name="candidate_exam_appearing"
                      value={formData.candidate_exam_appearing}
                      onChange={handleChange}
                      className={fieldClass}
                    >
                      <option value="">Select Exam</option>
                      <option value="CET">CET</option>
                      <option value="JEE">JEE</option>
                      <option value="NEET">NEET</option>
                      <option value="Other">Other</option>
                    </select>
                  ) : (
                    <p className="text-gray-900">{formData.candidate_exam_appearing || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rank (if available)
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="candidate_rank"
                      value={formData.candidate_rank}
                      onChange={handleChange}
                      min="1"
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_rank || 'Not provided'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Preferred Colleges and Courses */}
            <div className="bg-primary-100 rounded-lg shadow-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-6 flex items-center">
                <BookmarkIcon className="w-5 h-5 mr-2" />
                Preferred Colleges and Courses
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Colleges
                  </label>
                  {isEditing ? (
                    <select
                      multiple
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-40"
                      onChange={(e) => handleMultiChange('candidate_preferred_colleges', e.target.options)}
                    >
                      {colleges.map(college => (
                        <option key={college.id} value={college.id}>
                          {college.institute_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="space-y-1">
                      {formData.candidate_preferred_colleges.length > 0 ? (
                        formData.candidate_preferred_colleges.map((collegeId, index) => {
                          const college = colleges.find(c => c.id === collegeId);
                          return (
                            <span key={index} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-1 mb-1">
                              {college?.institute_name || collegeId}
                            </span>
                          );
                        })
                      ) : (
                        <p className="text-gray-500">No colleges selected</p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Courses/Branches
                  </label>
                  {isEditing ? (
                    <select
                      multiple
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-40"
                      onChange={(e) => handleMultiChange('candidate_preferred_courses', e.target.options)}
                    >
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.branch_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="space-y-1">
                      {formData.candidate_preferred_courses.length > 0 ? (
                        formData.candidate_preferred_courses.map((courseId, index) => {
                          const course = courses.find(c => c.id === courseId);
                          return (
                            <span key={index} className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mr-1 mb-1">
                              {course?.branch_name || courseId}
                            </span>
                          );
                        })
                      ) : (
                        <p className="text-gray-500">No courses selected</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred States
                  </label>
                  {isEditing ? (
                    <select
                      multiple
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32"
                      onChange={(e) => handleMultiChange('candidate_preferred_state', e.target.options)}
                    >
                      {states.map(state => (
                        <option key={state.id} value={state.id}>
                          {state.state_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="space-y-1">
                      {formData.candidate_preferred_state.length > 0 ? (
                        formData.candidate_preferred_state.map((stateId, index) => {
                          const state = states.find(s => s.id === stateId);
                          return (
                            <span key={index} className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full mr-1 mb-1">
                              {state?.state_name || stateId}
                            </span>
                          );
                        })
                      ) : (
                        <p className="text-gray-500">No states selected</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Family Information */}
            <div className="bg-primary-100 rounded-lg shadow-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-6 flex items-center">
                <UserIcon className="w-5 h-5 mr-2" />
                Family Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Father's Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="candidate_father_name"
                      value={formData.candidate_father_name}
                      onChange={handleChange}
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_father_name || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mother's Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="candidate_mother_name"
                      value={formData.candidate_mother_name}
                      onChange={handleChange}
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_mother_name || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Father's Occupation
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="candidate_father_occupation"
                      value={formData.candidate_father_occupation}
                      onChange={handleChange}
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_father_occupation || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mother's Occupation
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="candidate_mother_occupation"
                      value={formData.candidate_mother_occupation}
                      onChange={handleChange}
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_mother_occupation || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Annual Family Income
                  </label>
                  {isEditing ? (
                    <select
                      name="candidate_income"
                      value={formData.candidate_income}
                      onChange={handleChange}
                      className={fieldClass}
                    >
                      <option value="">Select Income Range</option>
                      <option value="Under 1 Lakh">Under 1 Lakh</option>
                      <option value="1-3 Lakhs">1-3 Lakhs</option>
                      <option value="3-5 Lakhs">3-5 Lakhs</option>
                      <option value="5-10 Lakhs">5-10 Lakhs</option>
                      <option value="Above 10 Lakhs">Above 10 Lakhs</option>
                    </select>
                  ) : (
                    <p className="text-gray-900">{formData.candidate_income || 'Not provided'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="bg-primary-100 rounded-lg shadow-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-6 flex items-center">
                <MapPinIcon className="w-5 h-5 mr-2" />
                Address Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  {isEditing ? (
                    <textarea
                      name="candidate_address"
                      value={formData.candidate_address}
                      onChange={handleChange}
                      rows="3"
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_address || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="candidate_city"
                      value={formData.candidate_city}
                      onChange={handleChange}
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_city || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pincode
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="candidate_pincode"
                      value={formData.candidate_pincode}
                      onChange={handleChange}
                      className={fieldClass}
                    />
                  ) : (
                    <p className="text-gray-900">{formData.candidate_pincode || 'Not provided'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Quick Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Summary */}
            <div className="bg-primary-100 rounded-lg shadow-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Profile Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Profile Completion</span>
                  <span className="text-sm font-medium text-blue-600">
                    {Math.round((Object.values(formData).filter(value => value && value !== '').length / Object.keys(formData).length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{width: `${(Object.values(formData).filter(value => value && value !== '').length / Object.keys(formData).length) * 100}%`}}
                  ></div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-primary-100 rounded-lg shadow-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/capzone')}
                  className="w-full text-left p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="w-5 h-5 text-blue-600 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">Browse Colleges</p>
                      <p className="text-sm text-gray-600">Find your perfect college</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => navigate('/community')}
                  className="w-full text-left p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <UserIcon className="w-5 h-5 text-green-600 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">Join Community</p>
                      <p className="text-sm text-gray-600">Connect with other students</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => navigate('/student-dashboard')}
                  className="w-full text-left p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <AcademicCapIcon className="w-5 h-5 text-purple-600 mr-3" />
                    <div>
                      <p className="font-medium text-gray-900">Student Dashboard</p>
                      <p className="text-sm text-gray-600">View your dashboard</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateProfile;
